import React, { useEffect, useState, useRef } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import {
  Col,
  Row,
  Form as BForm,
  Button,
  Tabs,
  Tab,
} from "@themesberg/react-bootstrap";
import { createPromotionSchema } from "../schema";
import useCreatePromotions from "../hooks/useCreatePromotions";
import useCheckPermission from "../../../../utils/checkPermission";
import Trigger from "../../../../components/OverlayTrigger";
import { faImages, faUpload, faTrash, faImage } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GalleryModal } from "../../../../components/ConfirmationModal";
import { AdminsRoutes } from "../../../../routes";
import { CreatePromotionsTemplate } from "../../../../components/EditPromotionsTemplate/CreatePromotionsTemplate";
import { toast } from "../../../../components/Toast";
import { promotionCategories } from "../constants";
import NotificationIconContainer from "../../../../components/NotificationIcon/NotificationIconContainer";
import styled from 'styled-components';
import PageBackButton from '../../../../components/PageBackButton'


// Styled Components for Modern File Input
const FileUploadContainer = styled.div`
  position: relative;
  width: 100%;
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${props => props.isDragOver ? '#007bff' : '#dee2e6'};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  background: ${props => props.isDragOver ? '#f8f9ff' : '#fafbfc'};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    border-color: #007bff;
    background: #f8f9ff;
  }
  
  &.has-file {
    border-color: #28a745;
    background: #f8fff9;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadIcon = styled.div`
  font-size: 2.5rem;
  color: #6c757d;
  margin-bottom: 1rem;
  
  ${FileUploadArea}:hover & {
    color: #007bff;
  }
  
  ${FileUploadArea}.has-file & {
    color: #28a745;
  }
`;

const UploadText = styled.div`
  h5 {
    color: #495057;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }
  
  p {
    color: #6c757d;
    margin-bottom: 0;
    font-size: 0.875rem;
  }
`;

const PreviewSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;
`;

const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #e9ecef;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
  
  &:hover {
    background: #c82333;
  }
`;

const FileInfo = styled.div`
  background: #ffffff;
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .file-details {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .file-icon {
      color: #007bff;
    }
    
    .file-name {
      font-weight: 500;
      color: #495057;
    }
    
    .file-size {
      color: #6c757d;
      font-size: 0.875rem;
    }
  }
`;

const CreatePromotionsNew = ({ promotionsData }) => {
  const {
    navigate,
    createPromotions,
    selectedClient,
    selectedPortal,
    setSelectedClient,
    setSelectedPortal,
    galleryModal,
    setGalleryModal,
    getGalleryData,
    cmsKeys,
    selectedTab,
    setSelectedTab,
    languages,
    deleteCms,
    editPromotions,
  } = useCreatePromotions(promotionsData);
  const { isHidden } = useCheckPermission();
  const [title, setTitle] = useState({ EN: "" });
  const [content, setContent] = useState({ EN: "" });
  const [description, setDescription] = useState({ EN: "" });
  const [urlCheck, setUrlCheck] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleUrlCheck = (setFieldValue, values) => {
    setFieldValue("slug", "");
    setFieldValue("redirectUrlToggle", !values.redirectUrlToggle);
  };

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const validateImageDimensions = (file, width = 200, height = 200) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        if (img.width === width && img.height === height) {
          resolve(true);
        } else {
          reject(`Image must be ${width}x${height}px. Uploaded: ${img.width}x${img.height}px`);
        }
      };

      img.onerror = () => reject("Invalid image file");
    });
  };


  const handleDrop = async (e, setFieldValue) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setFieldValue("image", file);
        // try {
        //   await validateImageDimensions(file, 200, 200); // check 200x200
        //   setFieldValue("image", file);
        // } catch (err) {
        //   toast(err, "error");
        // }
      } else {
        toast("Please select an image file", "error");
      }
    }
  };


  const handleRemoveImage = (setFieldValue, e) => {
    e.preventDefault();
    e.stopPropagation();
    setFieldValue("image", "");
    // Reset the file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <div className="section-header-wrap mt-n3 mx-n3">
        <Row className="align-items-center justify-content-between">
          <Col sm={8}>
           <div className='backbtn'>
                       <PageBackButton />
            <h2 className="title-text">
              {promotionsData ? "Edit Promotions" : "Create Promotions"}
            </h2>
            </div>
          </Col>
          <Col xs={2} className="text-end">
            <NotificationIconContainer />
          </Col>
        </Row>
      </div>
      <div className="FormsBg">
        <Formik
          initialValues={{
            title: promotionsData ? promotionsData?.title?.EN : "",
            slug: promotionsData ? promotionsData?.slug : "",
            content: promotionsData ? promotionsData?.content?.EN : "",
            category: promotionsData ? promotionsData?.category : "1",
            description: promotionsData ? promotionsData?.description?.EN : "",
            image:
              promotionsData && promotionsData?.image !== null
                ? promotionsData?.image
                : "",
            url: promotionsData ? promotionsData?.url : "",
            redirectUrlToggle:
              promotionsData && promotionsData.length > 0 ? true : false,
          }}
          validationSchema={createPromotionSchema}
          onSubmit={(formValues) => {
            for (const lang in title) {
              if (
                [undefined, ""].includes(content?.[lang]) &&
                [undefined, ""].includes(title?.[lang]) &&
                [undefined, ""].includes(description?.[lang])
              ) {
                delete title[lang];
                delete content[lang];
              }
            }
            console.log("on submit", {
              ...formValues,
              title,
              content,
              description,
            });
            if (promotionsData) {
              editPromotions({
                promotionsData: { ...formValues, title, content, description },
              });
            } else {
              createPromotions({
                promotionsData: { ...formValues, title, content, description },
              });
            }
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            handleBlur,
            setFieldValue,
          }) => {
            console.log("values", values);
            return (
              <Form>
                <Row className="mb-3 align-items-center">
                  <Col xs={6}>
                    <Col>
                      <BForm.Control
                        type="text"
                        name="slug"
                        placeholder="Enter Slug"
                        value={values.slug}
                        disabled={urlCheck}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <ErrorMessage
                        component="div"
                        name="slug"
                        className="text-danger"
                      />
                    </Col>
                  </Col>

                  <Col>
                    <BForm.Select
                      type="text"
                      name="category"
                      style={{ maxWidth: "200px" }}
                      value={values.category}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {promotionCategories.map(({ label, value }) => {
                        return <option key={value} value={value}>{label}</option>;
                      })}
                    </BForm.Select>
                    <ErrorMessage
                      component="div"
                      name="category"
                      className="text-danger"
                    />
                  </Col>

                  {values.category === "3" && (
                    <Col>
                      <BForm.Check
                        type="switch"
                        name="isActive"
                        label="Redirect Url (Third Party)"
                        defaultChecked={urlCheck}
                        value={urlCheck}
                        onChange={() => handleUrlCheck(setFieldValue, values)}
                        onBlur={handleBlur}
                      />
                    </Col>
                  )}
                </Row>

                <Col className="mb-3 align-items-center">
                  <Col className="d-grid">
                    <BForm.Label className="fw-semibold mb-3">
                      Promotion Card Image{" "}
                      <span className="text-danger">*</span>
                    </BForm.Label>

                    <FileUploadContainer>
                      {/* Hidden File Input */}
                      <HiddenFileInput
                        ref={fileInputRef}
                        id="file-input"
                        name="image"
                        type="file"
                        accept="image/*"
                        onChange={async (event) => {
                          const file = event.currentTarget.files[0];
                          if (file) {
                            if (file) {
                              setFieldValue("image", file);
                            }
  
                            // try {
                            //   await validateImageDimensions(file, 200, 200); // check 200x200
                            //   setFieldValue("image", file);
                            // } catch (err) {
                            //   toast(err, "error");
                            //   // Reset file input if invalid
                            //   event.target.value = "";
                            // }
                          }
                        }}
                      />


                      {/* Upload Area */}
                      <FileUploadArea
                        className={values?.image ? 'has-file' : ''}
                        isDragOver={isDragOver}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, setFieldValue)}
                        onClick={handleFileInputClick}
                      >
                        <UploadIcon>
                          <FontAwesomeIcon
                            icon={values?.image ? faImage : faUpload}
                          />
                        </UploadIcon>

                        <UploadText>
                          {values?.image ? (
                            <>
                              <h5>Image Selected</h5>
                              <p>Click to change or drag & drop a new image</p>
                            </>
                          ) : (
                            <>
                              <h5>Upload Promotion Image</h5>
                              <p>Drag & drop an image here, or click to browse</p>
                              <p><small>Supports: JPG, PNG, GIF (Max: 5MB)</small></p>
                            </>
                          )}
                        </UploadText>
                      </FileUploadArea>

                      {/* Preview Section - Completely Separate */}
                      {values?.image && (
                        <PreviewSection>
                          <h6 className="mb-3">Preview:</h6>
                          <ImagePreviewContainer>
                            <PreviewImage
                              alt="Preview"
                              src={
                                typeof values?.image === "string"
                                  ? `${process.env.REACT_APP_S3_URL}/${values?.image}`
                                  : URL.createObjectURL(values?.image)
                              }
                            />
                            <RemoveButton
                              type="button"
                              onClick={(e) => handleRemoveImage(setFieldValue, e)}
                              title="Remove image"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </RemoveButton>
                          </ImagePreviewContainer>

                          {typeof values?.image !== "string" && (
                            <FileInfo>
                              <div className="file-details">
                                <FontAwesomeIcon icon={faImage} className="file-icon" />
                                <span className="file-name">{values.image.name}</span>
                                <span className="file-size">({formatFileSize(values.image.size)})</span>
                              </div>
                            </FileInfo>
                          )}
                        </PreviewSection>
                      )}
                    </FileUploadContainer>

                    <ErrorMessage
                      component="div"
                      name="image"
                      className="text-danger mt-2"
                    />
                  </Col>

                  {values.category.toString() === "3" &&
                    values.redirectUrlToggle && (
                      <Col className="m-3">
                        <Col xs={2}>
                          <BForm.Label>
                            Redirect Url <span className="text-danger">* </span>
                          </BForm.Label>
                        </Col>

                        <Col xs={5}>
                          <BForm.Control
                            type="text"
                            name="url"
                            placeholder="Enter Redirect Url"
                            value={values.url}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage
                            component="div"
                            name="url"
                            className="text-danger"
                          />
                        </Col>
                      </Col>
                    )}
                </Col>

                <div className="mt-5">
                  <CreatePromotionsTemplate
                    cmsKeys={cmsKeys}
                    setFieldValue={setFieldValue}
                    isHidden={isHidden}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    navigate={navigate}
                    deleteCms={deleteCms}
                    initValues={values}
                    errors={errors}
                    title={title}
                    setTitle={setTitle}
                    content={content}
                    setContent={setContent}
                    setDescription={setDescription}
                    description={description}
                    values={values}
                  />
                </div>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-warning"
                    className="m-2"
                    onClick={() => {
                      navigate(AdminsRoutes.PROMOTIONS);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline-success"
                    hidden={isHidden({ module: { key: "CMS", value: "U" } })}
                    onClick={() => {
                      if (content?.EN === "" && !values.redirectUrlToggle) {
                        toast("Content is Required", "error");
                      } else {
                        if (title?.EN === "") {
                          toast("Title is Required", "error");
                          window.scroll(0, 0);
                        } else if (errors.slug) {
                          window.scroll(0, 0);
                          handleSubmit();
                        } else {
                          handleSubmit();
                        }
                      }
                    }}
                    className="m-2"
                  >
                    {promotionsData ? "Submit" : "Create"}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      {galleryModal && (
        <GalleryModal
          galleryModal={galleryModal}
          setGalleryModal={setGalleryModal}
        />
      )}
    </>
  );
};

export default CreatePromotionsNew;
